import {
  PLAYBOOK_GENERATION_RUN_STATUS,
  type PlaybookGenerationRunStatus,
  PLAYBOOKS_WIZARD_STEP,
  type PlaybooksWizardStep,
} from 'src/data/performanceInsights/const';

// A failed run cannot be carried forward — the wizard offers its own state and only lets it be
// discarded, which frees the workspace's one active run for a fresh start.
export const isPlaybookGenerationRunFailed = (status?: PlaybookGenerationRunStatus): boolean =>
  status === PLAYBOOK_GENERATION_RUN_STATUS.failed;

const RUN_STATUS_TO_STEP: Record<PlaybookGenerationRunStatus, PlaybooksWizardStep> = {
  [PLAYBOOK_GENERATION_RUN_STATUS.collectingContext]: PLAYBOOKS_WIZARD_STEP.context,
  [PLAYBOOK_GENERATION_RUN_STATUS.contextLocked]: PLAYBOOKS_WIZARD_STEP.context,
  [PLAYBOOK_GENERATION_RUN_STATUS.generatingAudiences]: PLAYBOOKS_WIZARD_STEP.audiences,
  [PLAYBOOK_GENERATION_RUN_STATUS.audiencesReady]: PLAYBOOKS_WIZARD_STEP.audiences,
  [PLAYBOOK_GENERATION_RUN_STATUS.generatingPlaybooks]: PLAYBOOKS_WIZARD_STEP.playbooks,
  [PLAYBOOK_GENERATION_RUN_STATUS.playbooksReady]: PLAYBOOKS_WIZARD_STEP.playbooks,
  [PLAYBOOK_GENERATION_RUN_STATUS.approved]: PLAYBOOKS_WIZARD_STEP.playbooks,
  // The step behind the failure state; nothing of it is reachable, but the stepper still needs a
  // position to mark.
  [PLAYBOOK_GENERATION_RUN_STATUS.failed]: PLAYBOOKS_WIZARD_STEP.audiences,
};

export const getPlaybooksWizardStepFromStatus = (
  status: PlaybookGenerationRunStatus,
  wizardSteps: PlaybooksWizardStep[]
): PlaybooksWizardStep => {
  const step = RUN_STATUS_TO_STEP[status];
  // A channel can skip the step a status names. An organic run has no audiences step, and a failed
  // one marks the step behind it. Every channel starts at context.
  return wizardSteps.includes(step) ? step : PLAYBOOKS_WIZARD_STEP.context;
};

const PROCESSING_STATUSES: PlaybookGenerationRunStatus[] = [
  PLAYBOOK_GENERATION_RUN_STATUS.collectingContext,
  PLAYBOOK_GENERATION_RUN_STATUS.contextLocked,
  PLAYBOOK_GENERATION_RUN_STATUS.generatingAudiences,
  PLAYBOOK_GENERATION_RUN_STATUS.generatingPlaybooks,
];

export const isPlaybookGenerationRunProcessing = (
  status?: PlaybookGenerationRunStatus
): boolean => {
  if (!status) {
    return false;
  }
  return PROCESSING_STATUSES.includes(status);
};

// An approved run is finished: its playbooks are the page's content, so there is nothing left to
// resume. It stays the workspace's current run even after every playbook is deleted, which is why
// "has a run" is not the same question as "has work in progress".
export const isPlaybookGenerationRunResumable = (status?: PlaybookGenerationRunStatus): boolean =>
  Boolean(status) && status !== PLAYBOOK_GENERATION_RUN_STATUS.approved;
